// src/components/Navbar.tsx - Refactored Navbar with extracted components
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../hooks/useNavigation';
import DesktopNavigation from './Navigation/DesktopNavigation';
import MobileNavigation from './Navigation/MobileNavigation';
import FeatureModals from './Navigation/FeatureModals';
import InsurerDashboardTabNav from './Navigation/InsurerDashboardTabNav';
import AdminDashboardTabNav from './Navigation/AdminDashboardTabNav';
import { isInsurerDashboardArea } from '../utils/insurerDashboardTabs';
import { isAdminDashboardArea } from '../utils/adminDashboardTabs';
import { isFlashAdmin } from '../utils/adminAccess';
import { useInsurerLogo } from '../hooks/useInsurerLogo';

const Navbar: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isInsurer = user?.role === 'INSURER';
  const { orgName } = useInsurerLogo();
  const showInsurerTabs =
    isInsurer && isInsurerDashboardArea(location.pathname);
  const showAdminTabs =
    isFlashAdmin(user) && isAdminDashboardArea(location.pathname);

  const {
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isUserDropdownOpen,
    setIsUserDropdownOpen,
    showFlashRTC,
    setShowFlashRTC,
    showExecutiveTraining,
    setShowExecutiveTraining,
    showISO27001,
    setShowISO27001,
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeFeature,
    setUpgradeFeature,
    userDropdownRef,
    mobileMenuRef,
    isActive,
    handleFeatureClick,
    handleLogout,
  } = useNavigation();

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            {isInsurer ? (
              <div className="flex items-center">
                <Link to="/insurer-dashboard" className="flex items-center">
                  <img
                    src="/flashailogo.png"
                    alt="Flash Emergency Management"
                    className="h-9 w-auto"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Link>
                <div className="h-6 w-px bg-gray-300 mx-3" />
                <Link to="/insurer-dashboard" className="flex flex-col">
                  <span className="text-sm font-bold text-[#0c1e40] leading-tight">
                    {orgName || 'Insurance Partner'}
                  </span>
                  <span className="text-[11px] text-gray-500 leading-tight">Insurer portal</span>
                </Link>
              </div>
            ) : (
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/flashailogo.png"
                  alt="Flash AI"
                  className="h-10 w-auto"
                  onError={(e) => {
                    // Fallback if logo doesn't exist
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Link>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden min-w-0 flex-1 md:flex">
          <DesktopNavigation
            isActive={isActive}
            handleFeatureClick={handleFeatureClick}
            isUserDropdownOpen={isUserDropdownOpen}
            setIsUserDropdownOpen={setIsUserDropdownOpen}
            userDropdownRef={userDropdownRef}
            handleLogout={handleLogout}
          />
          </div>

          {/* Mobile Navigation */}
          <MobileNavigation
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            isActive={isActive}
            handleFeatureClick={handleFeatureClick}
            handleLogout={handleLogout}
            mobileMenuRef={mobileMenuRef}
          />
        </div>
      </div>

      {showInsurerTabs && (
        <div className="md:hidden">
          <InsurerDashboardTabNav />
        </div>
      )}
      {showAdminTabs && (
        <div className="md:hidden">
          <AdminDashboardTabNav />
        </div>
      )}

      {/* Feature Modals */}
      <FeatureModals
        showFlashRTC={showFlashRTC}
        setShowFlashRTC={setShowFlashRTC}
        showExecutiveTraining={showExecutiveTraining}
        setShowExecutiveTraining={setShowExecutiveTraining}
        showISO27001={showISO27001}
        setShowISO27001={setShowISO27001}
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        upgradeFeature={upgradeFeature}
        setUpgradeFeature={setUpgradeFeature}
      />
    </nav>
  );
};

export default Navbar;